import { useMutation } from '@apollo/client';
import {
    UpdateTransactionEntryDocument,
    UpdateTransactionEntryMutationVariables,
} from '../../graphql/generated';
import { typedKeys } from '../../util/objects';
import { TrackFcn, TrackingEventName } from '../tracking';
import { CreateRuleToastProps } from './rules';
import { CashflowTransactionEntry } from './types';

const FIELD_TO_EVENT_NAME_MAP: Record<
    Exclude<
        keyof UpdateTransactionEntryMutationVariables,
        'transactionEntryId'
    >,
    TrackingEventName
> = {
    categoryId: 'Transaction Entry Category Updated',
    merchantId: 'Transaction Entry Merchant Updated',
    isHidden: 'Transaction Entry Hidden',
    deletedAt: 'Transaction Entry Deleted',
    notes: 'Transaction Entry Notes Updated',
    date: 'Transaction Entry Date Updated',
};

export const useBaseUpdateTransactionEntry = ({
    transactionEntry,
    showCreateRuleToast,
    showError,
    showSuccess,
    track,
}: {
    transactionEntry: CashflowTransactionEntry;
    showCreateRuleToast: (args: CreateRuleToastProps) => void;
    showError: VoidFunction;
    showSuccess?: VoidFunction;
    track: TrackFcn;
}) => {
    const [updateTransactionEntry] = useMutation(
        UpdateTransactionEntryDocument,
        {
            onCompleted: (data, mutationOptions) => {
                const mutationVariables = mutationOptions?.variables ?? {};

                const fieldForTracking = typedKeys(
                    FIELD_TO_EVENT_NAME_MAP
                ).find((field) => field in mutationVariables);

                if (fieldForTracking) {
                    track(FIELD_TO_EVENT_NAME_MAP[fieldForTracking]);
                }

                // Show a toast to create a rule when categoryId, merchantId, or isHidden is updated
                const fieldForToast = (
                    ['categoryId', 'merchantId', 'isHidden'] as const
                ).find((f) => f in mutationVariables);

                if (
                    fieldForToast &&
                    data.updateTransactionEntry?.transactionEntry
                ) {
                    if (
                        fieldForToast === 'isHidden' &&
                        !data.updateTransactionEntry.transactionEntry.isHidden
                    ) {
                        // Only show the toast when hiding a transaction - not when unhiding
                        return;
                    }

                    showCreateRuleToast({
                        before: transactionEntry,
                        after: data.updateTransactionEntry.transactionEntry,
                        field: fieldForToast,
                    });
                } else {
                    showSuccess?.();
                }
            },
            onError: showError,
        }
    );

    return updateTransactionEntry;
};
